import type { GameFacts, GameOutcome, Phase, StageCurve, StageStanding } from '@chess/api-model';
import { winPercent } from '@chess/api-insights';
import { endingResultView, middlegameResultView } from '@chess/api-ending-case';

// The stage result card's data (2026-10-08): how the stage started and
// ended for the reader as win chances, and the stage's classification, the
// one the 30-day result cards use — the ending's Won · Comeback · Drawn ·
// Blown win · Lost, the middlegame's Ahead · Comeback · Level · Blown
// advantage · Behind, and for the opening the standing of the position taken
// into the middlegame (the middlegame's start), Ahead · Level · Behind.

export interface StageResult {
  label: string;
  /** A colour token, e.g. `var(--viz-ending-won)`. */
  colour: string;
  /** One sentence on the result, from the reader's side. */
  description: string;
  /** The reader's win chance, 0–100, at the stage's first scored position. */
  start: number;
  /** Their win chance at its end: the last scored position when the game went on, else the result (0, 50, 100). */
  end: number;
}

const OUTCOME_PERCENT: Record<GameOutcome, number> = { win: 100, draw: 50, loss: 0 };
const NEXT: Partial<Record<Phase, Phase>> = { opening: 'middlegame', middlegame: 'ending' };

const OPENING_VIEWS: Record<StageStanding, Pick<StageResult, 'label' | 'colour' | 'description'>> = {
  winning: { label: 'Ahead', colour: 'var(--viz-ending-won)', description: 'You came out of the opening winning.' },
  level: { label: 'Level', colour: 'var(--viz-ending-drawn)', description: 'You came out of the opening level.' },
  lost: { label: 'Behind', colour: 'var(--viz-ending-lost)', description: 'You came out of the opening lost.' },
};

const standingOfOutcome = (outcome: GameOutcome): StageStanding => (outcome === 'win' ? 'winning' : outcome === 'loss' ? 'lost' : 'level');

function firstScore(curve: StageCurve): number | undefined {
  return curve.scores.find((s): s is number => s !== null);
}
function lastScore(curve: StageCurve): number | undefined {
  for (let i = curve.scores.length - 1; i >= 0; i--) {
    const s = curve.scores[i];
    if (s !== null) return s;
  }
  return undefined;
}

type Facts = Pick<GameFacts, 'outcome' | 'stageCurves' | 'middlegameStart' | 'middlegameResult' | 'endingResult'>;

/** The stage's result for a scanned game that reached it; undefined without the scores or the classification. */
export function stageResult(stage: Phase, facts: Facts): StageResult | undefined {
  const curve = facts.stageCurves?.[stage];
  if (!curve) return undefined;
  const first = firstScore(curve);
  if (first === undefined) return undefined;
  const next = NEXT[stage];
  const wentOn = !!(next && facts.stageCurves?.[next]);
  const last = lastScore(curve);
  const end = wentOn && last !== undefined ? winPercent(last) : OUTCOME_PERCENT[facts.outcome];
  const view = viewOf(stage, facts, wentOn);
  if (!view) return undefined;
  return { ...view, start: winPercent(first), end };
}

function viewOf(stage: Phase, facts: Facts, wentOn: boolean): Pick<StageResult, 'label' | 'colour' | 'description'> | undefined {
  switch (stage) {
    case 'opening': {
      const standing = wentOn ? facts.middlegameStart : standingOfOutcome(facts.outcome);
      return standing ? OPENING_VIEWS[standing] : undefined;
    }
    case 'middlegame':
      return facts.middlegameResult ? middlegameResultView(facts.middlegameResult) : undefined;
    case 'ending':
      return facts.endingResult ? endingResultView(facts.endingResult) : undefined;
  }
}
